/**
 * One outreach wave in full — what the owner approves.
 *
 * The owner reads the German rendering; the text that is sent (in the
 * recipient's language) stays folded behind the back-translation check's
 * verdict. Approve binds to the wave's content hash: if the seat resubmits
 * while the page is open, the server refuses and the page reloads the new
 * version. Per recipient the row expands to the persona brief, the reason
 * for the language, every step in German and the schedule/outcome. An unsent
 * step shows its place in the server's send plan – when, and why then. A step
 * may carry a PDF letter the owner signs (`LetterCard`); approval stays
 * closed until every letter that needs a signature is signed.
 */
import { Fragment, useState } from "react";

import type { OutreachMessage, PlanItem, Wave } from "../../api/b2g";
import { formatRelative } from "../../lib/formatRelative";
import {
  STEP_LABEL,
  checkTone,
  formatDayMonth,
  groupByRecipient,
  isRunningUpdate,
  languageName,
  latestAutoReply,
  letterBlock,
  letterCounts,
  recipientCc,
  recipientRevision,
  REVIEW_STATUSES,
  revisionCounts,
  messageLabel,
  messageTone,
  percent,
  waveStatusTone,
} from "../../lib/b2g";
import { draftKey, useDraft } from "../../lib/hooks/useDraft";
import { berlinDay, planLine, whenLabel } from "../../lib/sendPlan";
import { Markdown } from "../Markdown";
import { Pill } from "../Pill";
import { ShortId } from "../ShortId";
import { CountryLabel } from "./CountryLabel";
import { LetterCard } from "./LetterCard";

export type WaveAction = "approve" | "approve_drafts" | "request_changes" | "reject" | "pause" | "resume" | "stop";

export function WaveDetail({
  wave,
  plan,
  horizonDays,
  busy,
  onAction,
}: {
  wave: Wave;
  /** The send plan by message id. */
  plan: Map<string, PlanItem>;
  horizonDays: number;
  busy: boolean;
  /** Resolves true once the action went through (false: cancelled or failed). */
  onAction: (action: WaveAction, note?: string) => Promise<boolean>;
}) {
  const note = useDraft(draftKey("outreach-wave-note", wave.id, wave.version));
  const [open, setOpen] = useState<string | null>(null);
  const groups = groupByRecipient(wave.messages ?? []);
  const foreign = [...new Set((wave.messages ?? []).map((m) => m.language))].filter((l) => l !== "de");
  const decidable = wave.status === "pending_approval";
  const update = isRunningUpdate(wave);
  const revision = wave.revision && REVIEW_STATUSES.includes(wave.status) ? wave.revision : null;
  const counts = revisionCounts(wave.messages ?? []);
  const letters = letterCounts(wave.messages ?? []);
  const needSignature = letters.signed + letters.toSign;
  const blocked = letterBlock(wave.messages ?? []);
  const today = berlinDay(new Date());

  async function act(action: WaveAction) {
    const text = note.draft.trim();
    if (action === "request_changes" && !text) return;
    const done = await onAction(action, text || undefined);
    if (done && (action === "request_changes" || action === "reject")) note.clearDraft();
  }

  return (
    <div className="space-y-5">
      <div className="panel">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading text-lg font-semibold text-ink">{wave.title}</h2>
              <Pill value={wave.status} tone={waveStatusTone(wave.status)} />
              {wave.delivery === "drafts" ? <Pill value="drafts – you send" tone="signal" /> : null}
              {needSignature ? (
                <Pill
                  value={`${letters.signed} of ${needSignature} letters signed`}
                  tone={letters.toSign ? "signal" : "ok"}
                />
              ) : null}
              <span className="text-xs text-ink-low">
                <ShortId id={wave.id} /> · v{wave.version} · {wave.agent_slug ?? "seat"} · {wave.updated_at ? formatRelative(wave.updated_at) : ""}
              </span>
            </div>
            {wave.summary_md ? (
              <div className="mt-2">
                <Markdown text={wave.summary_md} />
              </div>
            ) : null}
            {wave.decision_note ? (
              <p className="text-sm mt-2">
                <span className="text-ink-low">Last decision note: </span>
                {wave.decision_note}
              </p>
            ) : null}
            {revision ? (
              <div className="callout callout--info mt-3">
                <div>
                  {revision.running ? (
                    <p>
                      <span className="font-semibold">Update of a running wave</span> (v{revision.from_version} → v
                      {wave.version}). {revision.kept} sent email{revision.kept === 1 ? "" : "s"} stay as they are. Nothing
                      more goes out until you decide.
                    </p>
                  ) : (
                    <p>
                      <span className="font-semibold">New version</span> (v{revision.from_version} → v{wave.version}).
                    </p>
                  )}
                  <p>
                    {counts.changed} changed · {counts.added} new · {revision.removed.length} people removed
                    {revision.removed.length
                      ? `: ${revision.removed.map((r) => r.name ?? r.email).join(", ")}`
                      : ""}
                  </p>
                  {revision.note ? (
                    <div className="mt-2">
                      <Markdown text={revision.note} />
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
            {decidable && blocked ? (
              <div role="status" className="callout callout--warn mt-3">
                <p>
                  <span className="font-semibold">{blocked}.</span> Open each letter below, sign it in Preview and save –
                  the seat attaches the signed file and approval opens here.
                </p>
              </div>
            ) : null}
            {foreign.length ? (
              <p className="text-xs text-ink-low mt-2">
                Machine-translated into {foreign.map(languageName).join(", ")} – every translation was read back by an
                independent check; you see the German.
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-2 items-end">
            {decidable ? (
              <>
                <button
                  type="button"
                  className="btn btn-main"
                  disabled={busy || !!blocked}
                  onClick={() => act("approve")}
                  title={blocked ?? undefined}
                >
                  Approve wave – the server sends it
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-second"
                  disabled={busy || !!blocked}
                  onClick={() => act("approve_drafts")}
                  title={
                    blocked ??
                    "Every email lands as a draft in the outreach mailbox; you send it from Outlook. Follow-ups follow your send."
                  }
                >
                  Approve as drafts – I send from Outlook
                </button>
              </>
            ) : null}
            {wave.status === "approved" || wave.status === "sending" ? (
              <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => act("pause")}>
                Pause
              </button>
            ) : null}
            {wave.status === "paused" ? (
              <button type="button" className="btn btn-sm btn-second" disabled={busy} onClick={() => act("resume")}>
                Resume
              </button>
            ) : null}
            {["approved", "sending", "paused"].includes(wave.status) || update ? (
              <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => act("stop")}>
                Stop wave
              </button>
            ) : null}
          </div>
        </div>
        {decidable ? (
          <div className="mt-4">
            <textarea
              className="input w-full"
              rows={2}
              placeholder="Note for the seat – required for “Request changes”"
              value={note.draft}
              onChange={(e) => note.setDraft(e.target.value)}
            />
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                className="btn btn-sm btn-second"
                disabled={busy || !note.draft.trim()}
                onClick={() => act("request_changes")}
              >
                Request changes
              </button>
              {update ? null : (
                <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => act("reject")}>
                  Reject
                </button>
              )}
            </div>
          </div>
        ) : null}
      </div>

      <div className="panel panel--flush overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Recipient</th>
              <th>Market</th>
              <th>Language</th>
              <th>Strategy</th>
              <th>Check</th>
              <th>First email (German)</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(({ contactId, first, steps }) => {
              const expanded = open === contactId;
              const worstCheck = steps.find((s) => s.check_status === "flagged" || s.check_status === "error")
                ?? steps.find((s) => s.check_status === "pending")
                ?? first;
              const latest = [...steps].reverse().find((s) => s.status !== "pending") ?? first;
              const autoReply = latestAutoReply(first);
              const changed = recipientRevision(steps);
              const next = steps.map((s) => plan.get(s.id)).find((i) => i?.at);
              const cc = recipientCc(steps);
              const stepLetters = letterCounts(steps);
              return (
                <Fragment key={contactId}>
                  <tr className="cursor-pointer align-top" onClick={() => setOpen(expanded ? null : contactId)}>
                    <td>
                      <div className="font-heading font-semibold text-ink">{first.recipient_name ?? first.recipient_email}</div>
                      <div className="text-xs text-ink-low">{first.recipient_role ?? ""}</div>
                      <div className="text-xs text-ink-low">{first.recipient_email}</div>
                      {cc.cc.length || cc.before ? (
                        <div className="text-xs text-ink-low">cc: {cc.cc.join(", ") || "–"}</div>
                      ) : null}
                      {cc.before ? (
                        <div className="text-xs text-ink-low line-through">was: {cc.before.join(", ") || "–"}</div>
                      ) : null}
                    </td>
                    <td className="text-sm">
                      <CountryLabel iso2={first.market_iso2} />
                    </td>
                    <td className="text-sm">
                      {languageName(first.language)}
                    </td>
                    <td className="text-sm text-ink-medium">{first.strategy}</td>
                    <td>
                      <Pill value={worstCheck.check_status} tone={checkTone(worstCheck.check_status)} />
                    </td>
                    <td className="min-w-[320px] max-w-[520px]">
                      {first.subject_de ? (
                        <div className="text-sm font-semibold text-ink">{first.subject_de}</div>
                      ) : null}
                      <div className="text-sm whitespace-pre-wrap">{first.body_de}</div>
                    </td>
                    <td>
                      <Pill value={messageLabel(latest)} tone={messageTone(latest)} />
                      {changed ? (
                        <div className="mt-1">
                          <Pill value={changed} tone="signal" />
                        </div>
                      ) : null}
                      {stepLetters.toSign ? (
                        <div className="text-xs text-warn-fg mt-1 whitespace-nowrap">letter · needs your signature</div>
                      ) : stepLetters.letters ? (
                        <div className="text-xs text-ink-low mt-1 whitespace-nowrap">
                          letter{stepLetters.signed ? " · signed" : ""}
                        </div>
                      ) : null}
                      {autoReply ? (
                        <div className="text-xs text-ink-low mt-1 whitespace-nowrap">
                          auto-reply{autoReply.resume_on ? ` · back ${formatDayMonth(autoReply.resume_on)}` : ""}
                        </div>
                      ) : null}
                      {next?.at ? (
                        <div className="text-xs text-ink-low mt-1 whitespace-nowrap tnum">
                          next ≈ {whenLabel(next.at, today)}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                  {expanded ? (
                    <tr>
                      <td colSpan={7} className="bg-surface-tint">
                        <RecipientDetail steps={steps} plan={plan} today={today} horizonDays={horizonDays} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {wave.reply_blocks?.length ? (
        <div className="panel">
          <h3 className="font-heading font-semibold mb-2">
            Reply blocks (sent verbatim, only name / slot / link vary)
          </h3>
          <div className="space-y-3">
            {wave.reply_blocks.map((b) => (
              <div key={b.id}>
                <div className="text-sm font-semibold">
                  {b.key} <span className="text-ink-low font-normal">– {b.purpose}</span>
                </div>
                <div className="text-sm whitespace-pre-wrap">{b.body_de}</div>
                <div className="text-xs text-ink-low">
                  Variants: {Object.keys(b.variants).map(languageName).join(", ") || "German only"}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {wave.strategy_stats?.length ? <StrategyTable stats={wave.strategy_stats} /> : null}
    </div>
  );
}

function RecipientDetail({
  steps,
  plan,
  today,
  horizonDays,
}: {
  steps: OutreachMessage[];
  plan: Map<string, PlanItem>;
  today: string;
  horizonDays: number;
}) {
  const first = steps[0];
  const autoReplies = first.auto_replies ?? [];
  return (
    <div className="grid md:grid-cols-2 gap-5 py-2">
      <div>
        <div className="text-xs text-ink-low mb-1">Persona brief</div>
        <Markdown text={first.persona_md} />
        {autoReplies.length ? (
          <>
            <div className="text-xs text-ink-low mt-3 mb-1">Automatic replies</div>
            <ul className="text-sm">
              {autoReplies.map((a) => (
                <li key={a.message_id}>
                  {a.received_at ? `Received ${formatRelative(a.received_at)}` : "Received"} – not an answer, follow-ups
                  {a.resume_on ? ` resume ${formatDayMonth(a.resume_on)}` : " continue"}
                </li>
              ))}
            </ul>
          </>
        ) : null}
        <div className="text-xs text-ink-low mt-3 mb-1">Why {languageName(first.language)}</div>
        <p className="text-sm">{first.language_reason}</p>
        {first.source_url ? (
          <a className="text-xs link" href={first.source_url} target="_blank" rel="noreferrer">
            Source ↗
          </a>
        ) : null}
      </div>
      <div className="space-y-4">
        {steps.map((s) => {
          const planned = s.sent_at ? undefined : plan.get(s.id);
          return (
            <div key={s.id}>
              <div className="text-xs text-ink-low">
                {STEP_LABEL[s.step]}
                {s.step > 0 && !planned ? ` · ${s.delay_days} days after the previous one` : ""}
                {s.sent_at
                  ? ` · sent ${formatRelative(s.sent_at)}`
                  : planned
                    ? ` · ${planLine(planned, today, horizonDays)}`
                    : s.not_before
                      ? ` · not before ${formatRelative(s.not_before)}`
                      : s.planned_not_before
                        ? ` · not before ${formatDayMonth(s.planned_not_before)} – the seat's date`
                        : ""}
                {s.revision && s.revision.state !== "unchanged" ? ` · ${s.revision.state} in this version` : ""}
              </div>
              {s.subject_de ? <div className="text-sm font-semibold">{s.subject_de}</div> : null}
              <div className="text-sm whitespace-pre-wrap">{s.body_de}</div>
              {s.revision?.state === "changed" ? (
                <details className="mt-1">
                  <summary className="text-xs text-ink-low cursor-pointer">Before (German)</summary>
                  {s.revision.subject_de ? <div className="text-sm font-semibold mt-1">{s.revision.subject_de}</div> : null}
                  <div className="text-sm whitespace-pre-wrap text-ink-low">{s.revision.body_de}</div>
                </details>
              ) : null}
              {s.letter ? (
                <LetterCard message={s} />
              ) : s.revision?.letter_changed ? (
                <div className="text-xs text-warn-fg mt-1">Letter removed in this version</div>
              ) : null}
              {s.lint_issues?.length ? (
                <div className="text-xs mt-1" style={{ color: "var(--c-yellow-dark)" }}>
                  <span className="font-semibold">Style hints</span>
                  <ul>
                    {s.lint_issues.map((i) => (
                      <li key={i}>{i}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {s.check_issues.length ? (
                <ul className="text-xs mt-1 text-danger">
                  {s.check_issues.map((i) => (
                    <li key={i}>{i}</li>
                  ))}
                </ul>
              ) : null}
              {s.language !== "de" ? (
                <details className="mt-1">
                  <summary className="text-xs text-ink-low cursor-pointer">
                    Sent text ({languageName(s.language)}) and back-translation
                  </summary>
                  {s.subject ? <div className="text-sm font-semibold mt-1">{s.subject}</div> : null}
                  <div className="text-sm whitespace-pre-wrap">{s.body}</div>
                  {s.back_translation ? (
                    <div className="text-xs text-ink-low whitespace-pre-wrap mt-1">↩ {s.back_translation}</div>
                  ) : null}
                </details>
              ) : null}
              {s.error ? <div className="text-xs mt-1 text-danger">{s.error}</div> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function StrategyTable({ stats }: { stats: NonNullable<Wave["strategy_stats"]> }) {
  return (
    <div className="panel panel--flush overflow-x-auto">
      <table className="table">
        <thead>
          <tr>
            <th>By</th>
            <th>Value</th>
            <th className="num">Contacted</th>
            <th className="num">Replied</th>
            <th className="num">Bounced</th>
            <th className="num">Reply rate</th>
          </tr>
        </thead>
        <tbody>
          {stats.map((s) => (
            <tr key={`${s.dimension}:${s.value}`}>
              <td className="text-sm text-ink-low">{s.dimension}</td>
              <td className="text-sm">{s.dimension === "language" ? languageName(s.value) : s.value}</td>
              <td className="num tnum">{s.contacted}</td>
              <td className="num tnum">{s.replied}</td>
              <td className="num tnum">{s.bounced}</td>
              <td className="num tnum">{percent(s.reply_rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
